import { useEffect, useRef, useState } from "react";
import { api, formatApiError, API_BASE, getToken } from "../../lib/api";
import { COLORS } from "../../lib/brand";
import { Upload, FileText, Trash2, Download, Plus } from "lucide-react";
import { toast } from "sonner";

export default function AdminFinance() {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [receipts, setReceipts] = useState([]);
  const [reconciliations, setReconciliations] = useState([]);
  const [debt, setDebt] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  async function loadAll() {
    try {
      const [r, rec, d] = await Promise.all([
        api.get("/admin/receipts", { params: { year } }),
        api.get("/admin/reconciliations"),
        api.get("/admin/debt-tracker"),
      ]);
      setReceipts(r.data || []);
      setReconciliations(rec.data || []);
      setDebt(d.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [year]);

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tag_year", year);
      fd.append("label", file.name);
      await api.post("/admin/receipts", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Receipt uploaded");
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function deleteReceipt(id) {
    if (!confirm("Delete this receipt?")) return;
    try {
      await api.delete(`/admin/receipts/${id}`);
      toast.success("Deleted");
      setReceipts(receipts.filter((r) => r.id !== id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function downloadReceipt(file) {
    try {
      const token = getToken();
      const resp = await fetch(`${API_BASE}/files/${file.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.original_filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download");
    }
  }

  return (
    <div className="space-y-8" data-testid="admin-finance-page">
      <header>
        <h1 className="text-3xl font-semibold text-[#1A1A1A]">Finance & Bookkeeping</h1>
        <p className="text-sm text-[#5C5C5C] mt-1">Receipt vault, account reconciliation, and debt-payoff progress.</p>
      </header>

      {/* Receipt vault */}
      <section className="bg-white rounded-xl border border-[#E8E2D5] shadow-sm">
        <div className="px-5 py-4 border-b border-[#E8E2D5] flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-medium text-[#1A1A1A]">Digital Receipt Log</h2>
            <p className="text-xs text-[#5C5C5C] mt-0.5">Upload receipts, tag by tax year, retrieve any time.</p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={year}
              onChange={(e) => setYear(e.target.value)}
              className="px-3 py-2 border border-[#E8E2D5] rounded-md text-sm"
              data-testid="receipts-year-select"
            >
              {[2026, 2025, 2024, 2023].map((y) => (
                <option key={y} value={String(y)}>{y}</option>
              ))}
            </select>
            <label
              className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-white cursor-pointer"
              style={{ background: COLORS.maroon }}
            >
              <Upload size={14} />
              {uploading ? "Uploading..." : "Upload Receipt"}
              <input ref={fileRef} type="file" hidden onChange={handleUpload} disabled={uploading} data-testid="receipt-upload-input" />
            </label>
          </div>
        </div>
        {receipts.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-[#5C5C5C]">No receipts tagged {year} yet.</div>
        ) : (
          <ul className="divide-y divide-[#E8E2D5]">
            {receipts.map((r) => (
              <li key={r.id} className="px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <FileText size={18} className="text-[#800000] shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#1A1A1A] truncate">{r.label || r.original_filename}</div>
                    <div className="text-xs text-[#5C5C5C]">{(r.size / 1024).toFixed(1)} KB · {new Date(r.created_at).toLocaleDateString()}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={() => downloadReceipt(r)} className="text-sm text-[#800000] hover:text-[#5C0000] flex items-center gap-1" data-testid={`receipt-download-${r.id}`}>
                    <Download size={14} /> Download
                  </button>
                  <button onClick={() => deleteReceipt(r.id)} className="text-sm text-[#5C5C5C] hover:text-[#800000]" data-testid={`receipt-delete-${r.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Reconciliations */}
      <ReconciliationCard items={reconciliations} onAdd={loadAll} />

      {/* Debt tracker */}
      {debt && <DebtTrackerCard debt={debt} onSave={loadAll} />}
    </div>
  );
}

function ReconciliationCard({ items, onAdd }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ week_of: "", account: "", reconciled: true, notes: "" });
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/reconciliations", form);
      toast.success("Logged");
      setForm({ week_of: "", account: "", reconciled: true, notes: "" });
      setOpen(false);
      onAdd();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="bg-white rounded-xl border border-[#E8E2D5] shadow-sm" data-testid="reconciliation-card">
      <div className="px-5 py-4 border-b border-[#E8E2D5] flex items-center justify-between">
        <div>
          <h2 className="font-medium text-[#1A1A1A]">Account Reconciliation Log</h2>
          <p className="text-xs text-[#5C5C5C] mt-0.5">Weekly reconciliations of bank accounts vs. Notary Assist / CRM.</p>
        </div>
        <button onClick={() => setOpen(!open)} className="text-sm text-white px-3 py-2 rounded-md flex items-center gap-1" style={{ background: COLORS.maroon }} data-testid="recon-add-btn">
          <Plus size={14} /> Log
        </button>
      </div>
      {open && (
        <form onSubmit={submit} className="p-5 border-b border-[#E8E2D5] grid grid-cols-1 md:grid-cols-2 gap-3">
          <input type="date" required value={form.week_of} onChange={(e) => setForm({ ...form, week_of: e.target.value })} className="px-3 py-2 border border-[#E8E2D5] rounded-md text-sm" data-testid="recon-week-input" />
          <input placeholder="Account (e.g., Chase Business)" required value={form.account} onChange={(e) => setForm({ ...form, account: e.target.value })} className="px-3 py-2 border border-[#E8E2D5] rounded-md text-sm" data-testid="recon-account-input" />
          <textarea placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="md:col-span-2 px-3 py-2 border border-[#E8E2D5] rounded-md text-sm" />
          <button disabled={busy} className="md:col-span-2 px-4 py-2 text-white rounded-md text-sm" style={{ background: COLORS.maroon }} data-testid="recon-submit-btn">
            {busy ? "Saving..." : "Save"}
          </button>
        </form>
      )}
      {items.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-[#5C5C5C]">No reconciliations logged.</div>
      ) : (
        <ul className="divide-y divide-[#E8E2D5]">
          {items.map((r) => (
            <li key={r.id} className="px-5 py-3 flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-[#1A1A1A]">{r.account} · week of {r.week_of}</div>
                {r.notes && <div className="text-xs text-[#5C5C5C] mt-0.5">{r.notes}</div>}
              </div>
              <span className="text-xs px-2.5 py-1 rounded-full" style={{ background: COLORS.cream, color: COLORS.maroon }}>
                {r.reconciled ? "Reconciled" : "Pending"}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function DebtTrackerCard({ debt, onSave }) {
  const [cards, setCards] = useState(debt.cards || []);
  const [goalLabel, setGoalLabel] = useState(debt.goal_label || "");
  const [targetYear, setTargetYear] = useState(debt.target_year || 2026);
  const [busy, setBusy] = useState(false);

  function updateCard(idx, field, value) {
    const next = [...cards];
    next[idx] = { ...next[idx], [field]: field === "name" ? value : Number(value) };
    setCards(next);
  }

  function removeCard(idx) {
    setCards(cards.filter((_, i) => i !== idx));
  }

  async function save() {
    setBusy(true);
    try {
      await api.put("/admin/debt-tracker", { goal_label: goalLabel, target_year: targetYear, cards });
      toast.success("Saved");
      onSave();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  const totalStart = cards.reduce((s, c) => s + (Number(c.starting_balance) || 0), 0);
  const totalCurrent = cards.reduce((s, c) => s + (Number(c.current_balance) || 0), 0);
  const paid = Math.max(0, totalStart - totalCurrent);
  const pct = totalStart > 0 ? Math.min(100, (paid / totalStart) * 100) : 0;
  const fmt = (n) => `$${(Math.round(n) || 0).toLocaleString()}`;

  // Chart geometry — derive max for bar scaling
  const maxBal = Math.max(1, ...cards.map((c) => Math.max(Number(c.starting_balance) || 0, Number(c.current_balance) || 0)));

  return (
    <section className="bg-white rounded-xl border border-[#E8E2D5] shadow-sm" data-testid="debt-tracker-card">
      <div className="px-5 py-4 border-b border-[#E8E2D5] flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-medium text-[#1A1A1A]">Debt Payoff Tracker</h2>
          <p className="text-xs text-[#5C5C5C] mt-0.5">
            Goal: <input
              className="text-[#1A1A1A] font-medium bg-transparent border-b border-dashed border-[#E8E2D5] focus:outline-none focus:border-[#800000]"
              value={goalLabel}
              onChange={(e) => setGoalLabel(e.target.value)}
              data-testid="debt-goal-label"
            />
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-[#5C5C5C]">Target:</span>
          <input
            type="number"
            value={targetYear}
            onChange={(e) => setTargetYear(Number(e.target.value))}
            className="w-20 px-2 py-1 border border-[#E8E2D5] rounded text-sm"
            data-testid="debt-target-year"
          />
        </div>
      </div>

      <div className="p-5 space-y-6">
        {/* Stat tiles */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="debt-stats">
          <StatTile label="Paid Down" value={fmt(paid)} accent={COLORS.gold} testid="stat-paid" />
          <StatTile label="Remaining" value={fmt(totalCurrent)} accent={COLORS.maroon} testid="stat-remaining" />
          <StatTile label="Progress" value={`${pct.toFixed(1)}%`} accent="#1A1A1A" testid="stat-progress" />
        </div>

        {/* Overall progress bar */}
        <div>
          <div className="flex items-center justify-between text-xs text-[#5C5C5C] mb-1.5">
            <span>{fmt(paid)} paid · {fmt(totalCurrent)} to go</span>
            <span>{fmt(totalStart)} starting · {pct.toFixed(0)}%</span>
          </div>
          <div className="h-3 w-full rounded-full bg-[#FBF8F1] overflow-hidden">
            <div
              className="h-full transition-all duration-700"
              style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${COLORS.maroon}, ${COLORS.gold})` }}
            />
          </div>
        </div>

        {/* Per-card breakdown */}
        {cards.length > 0 && (
          <div className="space-y-3" data-testid="debt-per-card">
            <h3 className="text-xs font-medium text-[#5C5C5C] uppercase tracking-wider">Per-Card Breakdown</h3>
            {cards.map((c, idx) => {
              const start = Number(c.starting_balance) || 0;
              const current = Number(c.current_balance) || 0;
              const cardPaid = Math.max(0, start - current);
              const cardPct = start > 0 ? Math.min(100, (cardPaid / start) * 100) : 0;
              return (
                <div key={idx} className="bg-[#FBF8F1]/50 border border-[#E8E2D5] rounded-lg p-4" data-testid={`debt-progress-${idx}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-medium text-sm text-[#1A1A1A]">{c.name || `Card ${idx + 1}`}</div>
                    <div className="text-xs text-[#5C5C5C]">{cardPct.toFixed(0)}%</div>
                  </div>
                  <div className="h-2 w-full rounded-full bg-white overflow-hidden">
                    <div
                      className="h-full transition-all duration-700"
                      style={{ width: `${cardPct}%`, background: `linear-gradient(90deg, ${COLORS.maroon}, ${COLORS.gold})` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-[#5C5C5C] mt-1.5">
                    <span>{fmt(cardPaid)} paid</span>
                    <span>{fmt(current)} of {fmt(start)} remaining</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Comparison bar chart */}
        {cards.length > 0 && (
          <div data-testid="debt-comparison-chart">
            <h3 className="text-xs font-medium text-[#5C5C5C] uppercase tracking-wider mb-3">Starting vs. Current Balance</h3>
            <div className="space-y-3">
              {cards.map((c, idx) => {
                const start = Number(c.starting_balance) || 0;
                const current = Number(c.current_balance) || 0;
                const startW = (start / maxBal) * 100;
                const currentW = (current / maxBal) * 100;
                return (
                  <div key={idx} className="space-y-1">
                    <div className="text-xs text-[#1A1A1A] font-medium">{c.name || `Card ${idx + 1}`}</div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#5C5C5C] w-16 shrink-0">Starting</span>
                      <div className="flex-1 h-3 bg-[#FBF8F1] rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${startW}%`, background: COLORS.maroon }} />
                      </div>
                      <span className="text-xs text-[#1A1A1A] font-medium w-20 text-right">{fmt(start)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#5C5C5C] w-16 shrink-0">Current</span>
                      <div className="flex-1 h-3 bg-[#FBF8F1] rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${currentW}%`, background: COLORS.gold }} />
                      </div>
                      <span className="text-xs text-[#1A1A1A] font-medium w-20 text-right">{fmt(current)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Edit cards */}
        <div className="pt-4 border-t border-[#E8E2D5]">
          <h3 className="text-xs font-medium text-[#5C5C5C] uppercase tracking-wider mb-3">Edit Balances</h3>
          <div className="space-y-2">
            {cards.map((c, idx) => (
              <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                <input value={c.name} onChange={(e) => updateCard(idx, "name", e.target.value)} placeholder="Card name" className="md:col-span-4 px-3 py-2 border border-[#E8E2D5] rounded-md text-sm" data-testid={`debt-card-name-${idx}`} />
                <input type="number" step="0.01" value={c.starting_balance} onChange={(e) => updateCard(idx, "starting_balance", e.target.value)} placeholder="Starting" className="md:col-span-3 px-3 py-2 border border-[#E8E2D5] rounded-md text-sm" data-testid={`debt-card-start-${idx}`} />
                <input type="number" step="0.01" value={c.current_balance} onChange={(e) => updateCard(idx, "current_balance", e.target.value)} placeholder="Current" className="md:col-span-3 px-3 py-2 border border-[#E8E2D5] rounded-md text-sm" data-testid={`debt-card-current-${idx}`} />
                <button
                  onClick={() => removeCard(idx)}
                  className="md:col-span-2 text-xs text-[#5C5C5C] hover:text-[#800000] py-2"
                  data-testid={`debt-card-remove-${idx}`}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-[#E8E2D5]">
          <button
            onClick={() => setCards([...cards, { name: `Card ${cards.length + 1}`, starting_balance: 0, current_balance: 0 }])}
            className="text-sm text-[#800000] hover:text-[#5C0000]"
            data-testid="debt-add-card"
          >
            + Add card
          </button>
          <button onClick={save} disabled={busy} className="px-4 py-2 rounded-md text-sm text-white" style={{ background: COLORS.maroon }} data-testid="debt-save-btn">
            {busy ? "Saving..." : "Save Progress"}
          </button>
        </div>
      </div>
    </section>
  );
}

function StatTile({ label, value, accent, testid }) {
  return (
    <div className="rounded-lg p-4 border border-[#E8E2D5] bg-white" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">{label}</div>
      <div className="text-2xl font-semibold mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}
